import {
  baseUrl as defaultBaseUrl,
  doLogout,
  refreshAccessToken
} from "@/api/config";
import { STATUS_CODE } from "@/constants/errorCode";
import {
  AnalysisStatus,
  type Dataset,
  type DatasetChunkStream,
  type DatasetData,
  type DatasetMetadataStream,
  type DatasetStreamDone
} from "@/types/dataset";
import { CustomError, UnauthorizedStreamError } from "@/utils/errors";
import { generateDatasetData } from "@/utils/generateDatasetData";

import { fetchEventSource } from "@microsoft/fetch-event-source";

export type StreamAnalysisResultOptions = {
  analysisId: string;
  /** Aborting settles the returned promise; it never hangs. */
  signal: AbortSignal;
  baseUrl?: string;
};

export type StreamedAnalysisDataset = {
  datasetData: DatasetData;
  dataset: Dataset;
};

export type StreamAnalysisResultResult = {
  /** The last status the stream declared, always terminal by the time it closes. */
  status: AnalysisStatus | undefined;
  /**
   * Every dataset the stream delivered — the result grid and the analysis's own
   * input datasets both. Which is which is the caller's to decide; the frames
   * describe them identically.
   */
  datasets: StreamedAnalysisDataset[];
};

const toAbortError = (signal: AbortSignal): unknown =>
  (signal.reason as unknown) ?? new DOMException("Aborted", "AbortError");

/**
 * Reads the `{ error: { reason, message, details } }` envelope off a failed
 * stream response, so the backend's own reason survives to the caller instead
 * of being flattened into a generic connection failure.
 */
const toStreamError = async (response: Response): Promise<CustomError> => {
  const payload = (await response
    .clone()
    .json()
    .catch(() => null)) as {
    error?: { reason?: string; message?: string; details?: unknown };
  } | null;

  const error = payload?.error;

  return new CustomError(
    error?.message ??
      `Unexpected response status: ${response.status.toString()}`,
    response.status,
    error?.details,
    error?.reason
  );
};

/**
 * Reads one finished analysis off geospatial's own status surface, resolving
 * with every dataset it delivered.
 *
 * The same endpoint the SDK follows for the stepper, read a second time for its
 * payload: a finished analysis re-answers it in full, which is how the history
 * page repaints a past run. Separate from `useAnalysisResult`, which reads the
 * identical frames but is a mounted hook driven by a Redux id — this is
 * promise-shaped because `loadAnalysisResult` has to await the draw before it
 * may tell the agent the map holds the result.
 */
const streamOnce = ({
  analysisId,
  signal,
  baseUrl
}: StreamAnalysisResultOptions): Promise<StreamAnalysisResultResult> =>
  new Promise((resolve, reject) => {
    // An already-aborted signal never fires `abort`, so without this guard the
    // promise would never settle and would strand the agent's pause.
    if (signal.aborted) {
      reject(toAbortError(signal));
      return;
    }

    /** dataset_id -> its `:meta` frame. One frame describes several datasets. */
    const metadataById: Record<string, DatasetMetadataStream> = {};
    const datasetsById: Record<string, StreamedAnalysisDataset> = {};
    /** Rows for the dataset currently streaming; every `:done` drains it. */
    const chunkBuffer: DatasetChunkStream["rows"] = [];
    let status: AnalysisStatus | undefined;
    let message: string | undefined;

    const controller = new AbortController();

    signal.addEventListener(
      "abort",
      () => {
        controller.abort();
        // `fetchEventSource` skips `onerror` when its own controller is
        // aborted, so nothing else would ever settle this promise.
        reject(toAbortError(signal));
      },
      { once: true }
    );

    /**
     * Collects one completed dataset. Both frame families carry the same shape,
     * so `analysis_result:done` and `dataset_data:done` are the same handler.
     */
    const collect = (done: DatasetStreamDone) => {
      const metadata = metadataById[done.dataset_id];

      // Rows without their description cannot be turned into a layer. Drop them
      // rather than fail: the result grid may still be described.
      if (!metadata) {
        chunkBuffer.length = 0;
        return;
      }

      const { datasetData, newDataset } = generateDatasetData(
        { dataset_id: done.dataset_id, rows: [...chunkBuffer], seq: 0 },
        metadata,
        true
      );

      datasetsById[done.dataset_id] = {
        datasetData,
        dataset: newDataset
      };
      chunkBuffer.length = 0;
    };

    fetchEventSource(
      `${baseUrl ?? defaultBaseUrl}/v1/analysis/${analysisId}/stream`,
      {
        method: "GET",
        credentials: "include",
        signal: controller.signal,
        openWhenHidden: true,

        async onopen(response: Response): Promise<void> {
          if (response.ok) return;

          if (response.status === STATUS_CODE.UNAUTHORIZED) {
            throw new UnauthorizedStreamError();
          }

          throw await toStreamError(response);
        },

        onmessage(msg) {
          const { event, data } = msg;

          if (!data) return;

          switch (event) {
            case "analysis_status": {
              const payload = JSON.parse(data);

              status = payload.status;
              if (payload.message) message = payload.message;
              break;
            }

            case "analysis_result:meta": {
              const frames = JSON.parse(data) as DatasetMetadataStream[];

              for (const frame of frames) {
                metadataById[frame.dataset_id] = frame;
              }
              break;
            }

            case "analysis_result:chunk":
            case "dataset_data:chunk": {
              const rows = JSON.parse(data).rows as DatasetChunkStream["rows"];

              // Avoid spread (`push(...rows)`): passing each row as a separate
              // argument throws "RangeError: Maximum call stack size exceeded"
              // for a large chunk. Loop-push has no such limit.
              for (const row of rows) chunkBuffer.push(row);
              break;
            }

            case "analysis_result:done":
            case "dataset_data:done": {
              collect(JSON.parse(data) as DatasetStreamDone);
              break;
            }

            case "stream:done": {
              if (status !== AnalysisStatus.SUCCESS) {
                // The SDK only calls in on a terminal SUCCESS, so a stream that
                // re-reads anything else describes a run with no result to draw.
                reject(
                  new CustomError(
                    message ??
                      `The analysis finished as ${status ?? "an unknown status"}.`,
                    STATUS_CODE.INTERNAL_SERVER_ERROR
                  )
                );
                controller.abort();
                break;
              }

              resolve({ status, datasets: Object.values(datasetsById) });
              controller.abort();
              break;
            }

            case "stream:error": {
              const payload = JSON.parse(data);

              reject(
                new CustomError(
                  payload.message ?? "The analysis stream failed.",
                  payload.code ?? STATUS_CODE.INTERNAL_SERVER_ERROR,
                  payload.details,
                  payload.reason
                )
              );
              controller.abort();
              break;
            }
          }
        },

        onerror(err) {
          // Reject, then rethrow so `fetchEventSource` disposes instead of
          // retrying on a one-second timer behind our back.
          reject(err);
          throw err;
        }
      }
    );
  });

/**
 * `streamOnce` plus the one retry an expired access token is owed.
 *
 * ponytail: no connection-level retry. A blip fails the draw, which the adapter
 * reports as `RESULT_LOAD_FAILED` — a failure the agent offers to retry, and
 * the settled card carries its own "load again" button. Add a bounded retry
 * here only if real streams turn out to drop often.
 */
export const streamAnalysisResult = async (
  options: StreamAnalysisResultOptions
): Promise<StreamAnalysisResultResult> => {
  try {
    return await streamOnce(options);
  } catch (error) {
    if (!(error instanceof UnauthorizedStreamError)) throw error;

    await refreshAccessToken();

    try {
      return await streamOnce(options);
    } catch (retryError) {
      if (retryError instanceof UnauthorizedStreamError) await doLogout();
      throw retryError;
    }
  }
};
